import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';

// Conteúdo de cada aba. Valores no formato { texto, href } viram links.
const ARQUIVOS = [
  {
    nome: 'perfil.json',
    dados: {
      nome: 'Gabriel Barbosa',
      localização: 'Guarulhos, São Paulo - Brasil',
      classificação: 'Desenvolvedor',
      nível: 'Pleno',
      formação: {
        técnico: 'Técnico em Informática - SENAC',
        graduação: 'Análise e Desenvolvimento de Sistemas - UNICSUL',
        pós_graduação: 'Offensive Cyber Security - FIAP',
      },
    },
  },
  {
    nome: 'stack.json',
    dados: {
      linguagens: ['PHP', 'JavaScript', 'Python', 'HTML', 'CSS', 'SQL'],
      frameworks: ['React', 'jQuery', 'Bootstrap', 'Tailwind CSS'],
      cms: ['WordPress'],
      ferramentas: [
        'Ferramentas de monitoramento de sites e servidores',
        'Integração de APIs',
        'Monitoramento de desempenho',
        'Análise de logs',
        'Tecnologias de servidor',
        'Scripts de automação',
      ],
      experiência: [
        'Integrações com APIs REST',
        'Automação de processos',
        'Integração/Entrega contínua (CI/CD)',
        'Gerenciamento de versões com Git',
        'Monitoramento de infraestrutura',
      ],
    },
  },
  {
    nome: 'contato.json',
    dados: {
      linkedin: { texto: 'linkedin.com/in/barbxsa', href: 'https://www.linkedin.com/in/barbxsa/' },
      github: { texto: 'github.com/barbxsa', href: 'https://github.com/barbxsa' },
      whatsapp: { texto: '+55 11 99424-6251', href: 'https://wa.me/5511994246251' },
      email: { texto: 'aopgabriel@gmail.com', href: 'mailto:aopgabriel@gmail.com' },
      currículo: { texto: 'curriculo2025.pdf', href: '/curriculo2025.pdf' },
    },
  },
];

const ehLink = (v) => v && typeof v === 'object' && 'href' in v;

// Transforma um objeto em linhas de tokens coloridos, no estilo JSON.stringify(obj, null, 2)
function tokenizar(dados) {
  const linhas = [];
  const push = (indent, segs) => linhas.push({ indent, segs });

  const walk = (val, indent, prefixo, virgula) => {
    const fim = virgula ? [{ t: 'punc', v: ',' }] : [];

    if (Array.isArray(val)) {
      const inline = val.join('').length + val.length * 4 <= 60;
      if (inline) {
        const segs = [...prefixo, { t: 'punc', v: '[' }];
        val.forEach((item, i) => {
          segs.push({ t: 'str', v: JSON.stringify(item) });
          if (i < val.length - 1) segs.push({ t: 'punc', v: ', ' });
        });
        push(indent, [...segs, { t: 'punc', v: ']' }, ...fim]);
      } else {
        push(indent, [...prefixo, { t: 'punc', v: '[' }]);
        val.forEach((item, i) => walk(item, indent + 1, [], i < val.length - 1));
        push(indent, [{ t: 'punc', v: ']' }, ...fim]);
      }
    } else if (val && typeof val === 'object' && !ehLink(val)) {
      const entradas = Object.entries(val);
      push(indent, [...prefixo, { t: 'punc', v: '{' }]);
      entradas.forEach(([k, v], i) =>
        walk(v, indent + 1, [{ t: 'key', v: JSON.stringify(k) }, { t: 'punc', v: ': ' }], i < entradas.length - 1)
      );
      push(indent, [{ t: 'punc', v: '}' }, ...fim]);
    } else if (ehLink(val)) {
      push(indent, [...prefixo, { t: 'str', v: JSON.stringify(val.texto), href: val.href }, ...fim]);
    } else {
      const t = typeof val === 'string' ? 'str' : 'bool';
      push(indent, [...prefixo, { t, v: JSON.stringify(val) }, ...fim]);
    }
  };

  walk(dados, 0, [], false);

  // posição de início de cada linha na contagem total de caracteres digitados
  let pos = 0;
  for (const linha of linhas) {
    linha.inicio = pos;
    pos += linha.segs.reduce((soma, s) => soma + s.v.length, 0);
  }
  return { linhas, total: pos };
}

// ---- sequência de entrada ----
// janela sobe inclinada e desfocada, com um brilho magenta que se apaga
const abrir = keyframes`
  0%   { opacity: 0; transform: perspective(1200px) translateY(60px) rotateX(18deg) scale(0.9); filter: blur(8px); }
  60%  { opacity: 1; filter: blur(0); }
  100% { opacity: 1; transform: none; filter: none; }
`;

const brilho = keyframes`
  0%   { box-shadow: 0 0 0 1px rgba(205, 15, 232, 0), 0 8px 20px rgba(0, 0, 0, 0.3); }
  35%  { box-shadow: 0 0 0 1px rgba(205, 15, 232, 0.8), 0 0 80px rgba(205, 15, 232, 0.45); }
  100% { box-shadow: 0 0 0 1px rgba(205, 15, 232, 0), 0 8px 20px rgba(0, 0, 0, 0.3); }
`;

// bolinhas do header "estouram"
const pop = keyframes`
  0%   { transform: scale(0); }
  70%  { transform: scale(1.3); }
  100% { transform: scale(1); }
`;

// título e abas deslizam para o lugar
const surgir = keyframes`
  from { opacity: 0; transform: translate(var(--dx, 0), var(--dy, 0)); }
  to   { opacity: 1; transform: none; }
`;

// área de código se revela de cima para baixo, com uma linha de "scan"
const desdobrar = keyframes`
  from { clip-path: inset(0 0 100% 0); }
  to   { clip-path: inset(0 0 0 0); }
`;

const varrer = keyframes`
  0%   { top: 0; opacity: 1; }
  85%  { opacity: 1; }
  100% { top: 100%; opacity: 0; }
`;

const REVELAR = '0.7s cubic-bezier(0.65, 0, 0.35, 1) 0.5s both';

const piscar = keyframes`
  50% { opacity: 0; }
`;

const CodeBlock = styled.div`
  max-width: 800px;
  margin: 2rem auto;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.3);
  animation: ${abrir} 1s cubic-bezier(0.16, 1, 0.3, 1) both, ${brilho} 2s ease-out both;

  @media (prefers-reduced-motion: reduce) {
    &, & *, & *::before, & *::after {
      animation: none !important;
    }
  }
`;

const CodeHeader = styled.div`
  background-color: #191A1F;
  padding: 0.75rem 1rem;
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const Titulo = styled.span`
  flex: 1;
  min-width: 0;
  text-align: center;
  font-size: 15px;
  color: #cfcfcf;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  --dy: -8px;
  animation: ${surgir} 0.5s ease-out 0.45s both;
`;

const Dots = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;

  &.menu {
    gap: 3px;
    @media (max-width: 575px) {
      display: none;
    }
  }
`;

const Dot = styled.span`
  height: 12px;
  width: 12px;
  border-radius: 50%;
  display: inline-block;
  background-color: ${(props) => props.color};
  animation: ${pop} 0.4s ease-out ${(props) => props.$delay}s both;
`;

const WhiteDot = styled.span`
  height: 4px;
  width: 4px;
  border-radius: 50%;
  display: inline-block;
  background-color: #fff;
`;

const Abas = styled.div`
  display: flex;
  background-color: #16171B;
  overflow-x: auto;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const Aba = styled.button`
  background: ${(props) => (props.$ativa ? '#1e1e1e' : 'transparent')};
  color: ${(props) => (props.$ativa ? '#fff' : '#8a8a8a')};
  position: relative;
  border: 0;
  padding: 0.7rem 1rem;
  font-family: inherit;
  font-size: 14px;
  white-space: nowrap;
  cursor: pointer;
  transition: color 0.2s, background 0.2s;
  --dx: -12px;
  animation: ${surgir} 0.45s ease-out ${(props) => 0.55 + props.$indice * 0.08}s both;

  /* linha magenta da aba ativa, que cresce ao trocar de aba */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: #CD0FE8;
    transform: scaleX(${(props) => (props.$ativa ? 1 : 0)});
    transition: transform 0.3s ease;
  }

  &:hover {
    color: #fff;
  }

  &:focus-visible {
    outline: 1px solid #CD0FE8;
    outline-offset: -1px;
  }
`;

const CodeContent = styled.div`
  padding: 1rem 1.5rem 1rem 0.75rem;
  font-size: 0.95rem;
  line-height: 1.6;
  background: #1e1e1e;
  cursor: ${(props) => (props.$digitando ? 'pointer' : 'default')};
  position: relative;
  animation: ${desdobrar} ${REVELAR};

  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    height: 2px;
    pointer-events: none;
    background: linear-gradient(90deg, transparent, #CD0FE8, transparent);
    box-shadow: 0 0 12px 2px rgba(205, 15, 232, 0.6);
    opacity: 0;
    animation: ${varrer} ${REVELAR};
  }
`;

const Linhas = styled.div`
  margin: 0;
  font-family: ui-monospace, SFMono-Regular, Consolas, 'Courier New', monospace;
  /* todas as abas ficam empilhadas na mesma célula: a altura sempre é a da maior */
  display: grid;

  @media (max-width: 575px) {
    font-size: 0.8rem;
  }
`;

const Camada = styled.div`
  grid-area: 1 / 1;
  visibility: ${(props) => (props.$fantasma ? 'hidden' : 'visible')};
`;

const Linha = styled.div`
  display: flex;
`;

// indentação via padding para que linhas quebradas mantenham o alinhamento
const Codigo = styled.span`
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  padding-left: ${(props) => props.$indent * 2}ch;
`;

const Numero = styled.span`
  color: #666;
  min-width: 2.5ch;
  margin-right: 1.25rem;
  text-align: right;
  user-select: none;
  flex-shrink: 0;
`;

const Cursor = styled.span`
  display: inline-block;
  width: 0.55em;
  height: 1.15em;
  margin-left: 1px;
  vertical-align: text-bottom;
  background: #CD0FE8;
  animation: ${(props) => (props.$digitando ? 'none' : piscar)} 1s steps(1) infinite;
`;

const CORES = {
  key: '#ff4999',
  str: '#81ffd9',
  punc: '#8b8b8b',
  bool: '#ffb86c',
};

const Link = styled.a`
  color: ${CORES.str};
  text-decoration: underline dotted;
  text-underline-offset: 3px;
  &:hover {
    color: #CD0FE8;
  }
`;

const reduzMovimento = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export default function JsonWindow() {
  const arquivos = useMemo(() => ARQUIVOS.map((a) => ({ ...a, ...tokenizar(a.dados) })), []);

  const [ativa, setAtiva] = useState(0);
  const [digitados, setDigitados] = useState(0);
  const concluidas = useRef(new Set());
  const primeira = useRef(true);
  const abasRef = useRef([]);

  const arquivo = arquivos[ativa];
  const digitando = digitados < arquivo.total;
  useEffect(() => {
    const { total } = arquivos[ativa];
    if (concluidas.current.has(ativa) || reduzMovimento()) {
      concluidas.current.add(ativa);
      setDigitados(total);
      return;
    }

    setDigitados(0);
    const atraso = primeira.current ? 1300 : 150;
    primeira.current = false;
    const duracao = Math.min(2600, Math.max(1200, total * 9));
    let frame;
    let inicio;

    const passo = (agora) => {
      if (concluidas.current.has(ativa)) return; // usuário pulou a animação
      if (!inicio) inicio = agora;
      const n = Math.min(total, Math.floor(((agora - inicio) / duracao) * total));
      setDigitados(n);
      if (n < total) frame = requestAnimationFrame(passo);
      else concluidas.current.add(ativa);
    };

    const timeout = setTimeout(() => {
      frame = requestAnimationFrame(passo);
    }, atraso);

    return () => {
      clearTimeout(timeout);
      cancelAnimationFrame(frame);
    };
  }, [ativa, arquivos]);

  const pular = () => {
    if (!digitando) return;
    concluidas.current.add(ativa);
    setDigitados(arquivo.total);
  };

  const onKeyAbas = (e) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    const dir = e.key === 'ArrowRight' ? 1 : -1;
    const prox = (ativa + dir + arquivos.length) % arquivos.length;
    setAtiva(prox);
    abasRef.current[prox]?.focus();
  };

  const visiveis = arquivo.linhas.filter((l, i) => i === 0 || l.inicio < digitados);

  const renderSegs = (linha, n) => {
    let restante = n - linha.inicio;
    return linha.segs.map((s, i) => {
      if (restante <= 0) return null;
      const texto = s.v.slice(0, restante);
      restante -= s.v.length;
      if (s.href) {
        const externo = s.href.startsWith('http');
        return (
          <Link key={i} href={s.href} target={externo ? '_blank' : undefined} rel={externo ? 'noreferrer' : undefined}>
            {texto}
          </Link>
        );
      }
      return <span key={i} style={{ color: CORES[s.t] }}>{texto}</span>;
    });
  };

  const textoCompleto = JSON.stringify(
    arquivo.dados,
    (_, v) => (ehLink(v) ? v.texto : v),
    2
  );

  return (
    <CodeBlock>
      <CodeHeader>
        <Dots>
          <Dot color="#ff5f56" $delay={0.3} />
          <Dot color="#ffbd2e" $delay={0.38} />
          <Dot color="#27c93f" $delay={0.46} />
        </Dots>
        <Titulo>gabriel-barbosa-portfolio.json</Titulo>
        <Dots className="menu" aria-hidden="true">
          <WhiteDot />
          <WhiteDot />
          <WhiteDot />
        </Dots>
      </CodeHeader>
      <Abas role="tablist" aria-label="Arquivos do portfólio" onKeyDown={onKeyAbas}>
        {arquivos.map((a, i) => (
          <Aba
            key={a.nome}
            ref={(el) => (abasRef.current[i] = el)}
            role="tab"
            id={`aba-${i}`}
            aria-selected={i === ativa}
            aria-controls="painel-json"
            tabIndex={i === ativa ? 0 : -1}
            $ativa={i === ativa}
            $indice={i}
            onClick={() => setAtiva(i)}
          >
            {a.nome}
          </Aba>
        ))}
      </Abas>
      <CodeContent
        id="painel-json"
        role="tabpanel"
        aria-labelledby={`aba-${ativa}`}
        $digitando={digitando}
        onClick={pular}
        title={digitando ? 'Clique para pular a animação' : undefined}
      >
        {/* versão completa para leitores de tela; a animada fica escondida deles */}
        <pre className="visually-hidden">{textoCompleto}</pre>
        <Linhas aria-hidden="true">
          {/* cópias invisíveis e completas de cada aba, só para reservar a altura */}
          {arquivos.map((a) => (
            <Camada key={a.nome} $fantasma>
              {a.linhas.map((linha, i) => (
                <Linha key={i}>
                  <Numero>{i + 1}</Numero>
                  <Codigo $indent={linha.indent}>{renderSegs(linha, a.total)}</Codigo>
                </Linha>
              ))}
            </Camada>
          ))}
          <Camada>
            {visiveis.map((linha, i) => (
              <Linha key={`${ativa}-${i}`}>
                <Numero>{i + 1}</Numero>
                <Codigo $indent={linha.indent}>
                  {renderSegs(linha, digitados)}
                  {i === visiveis.length - 1 && <Cursor $digitando={digitando} />}
                </Codigo>
              </Linha>
            ))}
          </Camada>
        </Linhas>
      </CodeContent>
    </CodeBlock>
  );
}
